import { Controller } from "@hotwired/stimulus"
import { showConfirmDialog, showAlert, reloadFolderSection } from "./confirm_dialog"

export default class extends Controller {
  static targets = [
    "content",
    "chevron",
    "toggleButton",
    "folderIconClosed",
    "folderIconOpen",
    "addMenu",
  ]
  static values = {
    expanded: Boolean,
    folderId: Number,
    folderName: String,
    depth: Number,
    maxDepth: Number,
    confirmTitle: String,
    confirmLabel: String,
    cancelLabel: String,
    moveConfirm: String,
    fileMoveConfirm: String,
    moveFailed: String,
    folderFallbackName: String,
    fileFallbackName: String,
  }

  connect() {
    this.updateUI()
    this._dragEnterCount = 0
    this._moveTimer = null
    this._moveActive = false
    this._deactivateTimeout = null
    this._closeAddMenuOnOutsideClick = this._closeAddMenuOnOutsideClick.bind(this)
  }

  disconnect() {
    this._clearMoveTimer()
    if (this._deactivateTimeout) clearTimeout(this._deactivateTimeout)
    document.removeEventListener("click", this._closeAddMenuOnOutsideClick)
  }

  toggleAddMenu(event) {
    event.stopPropagation()
    if (!this.hasAddMenuTarget) return

    const isHidden = this.addMenuTarget.classList.contains("hidden")
    this.addMenuTarget.classList.toggle("hidden")

    if (isHidden) {
      document.addEventListener("click", this._closeAddMenuOnOutsideClick)
    } else {
      document.removeEventListener("click", this._closeAddMenuOnOutsideClick)
    }
  }

  _closeAddMenuOnOutsideClick(event) {
    if (this.hasAddMenuTarget && !this.addMenuTarget.contains(event.target)) {
      this.addMenuTarget.classList.add("hidden")
      document.removeEventListener("click", this._closeAddMenuOnOutsideClick)
    }
  }

  toggle(event) {
    event.preventDefault()
    this.expandedValue = !this.expandedValue
    this.updateUI()
  }

  updateUI() {
    if (this.hasContentTarget) {
      if (this.expandedValue) {
        this.contentTarget.classList.remove("hidden")
      } else {
        this.contentTarget.classList.add("hidden")
      }
    }

    if (this.hasChevronTarget) {
      if (this.expandedValue) {
        this.chevronTarget.classList.add("rotate-90")
      } else {
        this.chevronTarget.classList.remove("rotate-90")
      }
    }

    // Toggle folder icon
    if (this.hasFolderIconClosedTarget && this.hasFolderIconOpenTarget) {
      if (this.expandedValue) {
        this.folderIconClosedTarget.classList.add("hidden")
        this.folderIconOpenTarget.classList.remove("hidden")
      } else {
        this.folderIconClosedTarget.classList.remove("hidden")
        this.folderIconOpenTarget.classList.add("hidden")
      }
    }
  }

  expandedValueChanged() {
    this.updateUI()
  }

  // The glyph is swapped by the totals turbo stream when a folder stops being empty.
  // The fresh markup always arrives in its closed state, so an expanded folder has to
  // re-apply the open/closed visibility once the replacement targets attach.
  folderIconClosedTargetConnected() {
    this.updateUI()
  }

  folderIconOpenTargetConnected() {
    this.updateUI()
  }

  // --- Drag-to-move handling ---

  _isValidDragTarget(draggedItem) {
    if (!draggedItem) return false

    const draggedFolderId = draggedItem.dataset.folderId
    const draggedFileId = draggedItem.dataset.fileId

    // Must be a folder or file
    if (!draggedFolderId && !draggedFileId) return false

    // Skip if dragging this folder onto itself
    if (draggedFolderId && parseInt(draggedFolderId) === this.folderIdValue) return false

    // Prevent dropping folders into folders at max depth
    if (draggedFolderId && this.depthValue >= this.maxDepthValue) return false

    // Skip if dragging a file that's already in this folder
    if (draggedFileId) {
      const fileFrame = draggedItem.closest("[data-document-folders--folder-folder-id-value]")
      if (
        fileFrame &&
        parseInt(fileFrame.dataset.documentFoldersFolderFolderIdValue) === this.folderIdValue
      )
        return false
    }

    return true
  }

  handleDragEnter(_event) {
    const draggedItem = this._getDraggedItem()
    if (!this._isValidDragTarget(draggedItem)) return

    // Cancel any pending deactivation (handles SortableJS DOM repositioning flicker)
    if (this._deactivateTimeout) {
      clearTimeout(this._deactivateTimeout)
      this._deactivateTimeout = null
    }

    this._dragEnterCount++
    if (!this._moveTimer && !this._moveActive) {
      this._startMoveTimer()
    }
  }

  handleDragOver(event) {
    const draggedItem = this._getDraggedItem()
    if (!this._isValidDragTarget(draggedItem)) return

    // Always allow drop so the browser fires the drop event
    event.preventDefault()
    event.stopPropagation()

    // Cancel any pending deactivation — cursor is still over this folder
    if (this._deactivateTimeout) {
      clearTimeout(this._deactivateTimeout)
      this._deactivateTimeout = null
    }

    // Backup: start move timer if dragEnter was missed (SortableJS flicker)
    if (!this._moveTimer && !this._moveActive) {
      this._startMoveTimer()
    }
  }

  handleDragLeave(_event) {
    this._dragEnterCount--
    if (this._dragEnterCount <= 0) {
      this._dragEnterCount = 0
      // Debounce deactivation — SortableJS causes rapid leave+enter cycles
      // when repositioning DOM elements during drag
      if (this._deactivateTimeout) clearTimeout(this._deactivateTimeout)
      this._deactivateTimeout = setTimeout(() => {
        if (this._dragEnterCount <= 0) {
          this._deactivateMove()
        }
      }, 100)
    }
  }

  async handleDrop(event) {
    if (!this._moveActive) return

    event.preventDefault()
    event.stopPropagation()

    const draggedItem = this._getDraggedItem()
    if (!draggedItem) {
      this._deactivateMove()
      return
    }

    const isFile = !!draggedItem.dataset.fileId
    const moveUrl = draggedItem.dataset.moveUrl
    const draggedName = isFile
      ? draggedItem.dataset.fileName || this.fileFallbackNameValue
      : draggedItem.dataset.folderName || this.folderFallbackNameValue
    const targetName = this.folderNameValue || this.folderFallbackNameValue
    const messageTemplate = isFile ? this.fileMoveConfirmValue : this.moveConfirmValue

    // Mark item so drag_controller skips reorder
    draggedItem.dataset.movedIntoFolder = "true"

    this._deactivateMove()

    const confirmed = await showConfirmDialog({
      title: this.confirmTitleValue,
      message: messageTemplate.replace("%{source}", draggedName).replace("%{target}", targetName),
      confirmLabel: this.confirmLabelValue,
      cancelLabel: this.cancelLabelValue,
    })

    if (!confirmed) {
      delete draggedItem.dataset.movedIntoFolder
      return
    }

    this._performMove(moveUrl, this.folderIdValue, isFile)
  }

  // --- Private helpers ---

  _getDraggedItem() {
    return (
      document.querySelector("[data-dragging='true'][data-folder-id]") ||
      document.querySelector("[data-dragging='true'][data-file-id]")
    )
  }

  _startMoveTimer() {
    this._clearMoveTimer()
    this._moveTimer = setTimeout(() => {
      this._activateMove()
    }, 500)
  }

  _clearMoveTimer() {
    if (this._moveTimer) {
      clearTimeout(this._moveTimer)
      this._moveTimer = null
    }
  }

  _activateMove() {
    this._moveActive = true
    this.element.classList.add("ring-2", "ring-blue-400", "ring-inset", "bg-blue-50")

    // Spring-loaded folder: auto-expand closed folders on hover
    if (!this.expandedValue) {
      this._wasAutoExpanded = true
      this.expandedValue = true
    }
  }

  _deactivateMove() {
    this._clearMoveTimer()
    if (this._deactivateTimeout) {
      clearTimeout(this._deactivateTimeout)
      this._deactivateTimeout = null
    }
    this._moveActive = false
    this._dragEnterCount = 0
    this.element.classList.remove("ring-2", "ring-blue-400", "ring-inset", "bg-blue-50")
  }

  _performMove(moveUrl, newParentId, isFile = false) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const body = isFile ? { new_folder_id: newParentId } : { new_parent_id: newParentId }

    fetch(moveUrl, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": token,
        Accept: "application/json",
      },
      body: JSON.stringify(body),
    })
      .then((response) => {
        if (response.ok) {
          reloadFolderSection(moveUrl)
        } else {
          response
            .json()
            .then((data) => {
              showAlert(data.error || this.moveFailedValue)
            })
            .catch(() => {
              showAlert(this.moveFailedValue)
            })
        }
      })
      .catch((error) => {
        console.error("Error moving item:", error)
        showAlert(this.moveFailedValue)
      })
  }
}
